import type { HeicWorkerRequest, HeicWorkerResponse } from "./heic.worker";

const QUALITY = 0.92;
// A malformed HEIC can wedge libheif without ever throwing; without this the
// upload/re-edit flow would await forever. Reject so callers surface an error.
const TIMEOUT_MS = 30_000;

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, { resolve: (b: Blob) => void; reject: (e: Error) => void }>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./heic.worker.ts", import.meta.url), { type: "module" });
  worker.onmessage = (e: MessageEvent<HeicWorkerResponse>) => {
    const { id, ok, blob, error } = e.data;
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (ok && blob) p.resolve(blob);
    else p.reject(new Error(error ?? "HEIC conversion failed"));
  };
  worker.onerror = (e) => {
    teardownWorker(e.message || "HEIC worker crashed");
  };
  return worker;
}

/**
 * Discard the singleton worker and fail every in-flight conversion.
 *
 * A wedged libheif can stop answering messages without ever throwing, so the
 * worker becomes permanently unusable. Reusing it would make every later
 * conversion in the tab time out too, so tear it down here — the next
 * ``getWorker()`` mints a fresh decoder.
 */
function teardownWorker(reason: string): void {
  const err = new Error(reason);
  for (const [, p] of pending) p.reject(err);
  pending.clear();
  worker?.terminate();
  worker = null;
}

export function convertHeicToJpeg(file: File, quality: number = QUALITY): Promise<Blob> {
  const w = getWorker();
  const id = ++seq;
  return new Promise<Blob>((resolve, reject) => {
    const timer = setTimeout(() => {
      // The worker is presumed wedged on libheif — discard it (and reject any
      // sibling conversions sharing it) so a later valid pick gets a fresh
      // decoder instead of reusing the stuck one.
      if (pending.has(id)) {
        teardownWorker("HEIC conversion timed out");
      }
    }, TIMEOUT_MS);
    pending.set(id, {
      resolve: (b) => {
        clearTimeout(timer);
        resolve(b);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      },
    });
    const req: HeicWorkerRequest = { id, blob: file, quality };
    w.postMessage(req);
  });
}
